import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { media } from '@atlaskit/primitives/responsive';

import { wacToken } from '@/libraries/design/foundations';

import {
	getHeroCardDesignImage,
	getHeroCardItImage,
	getHeroCardMarketingImage,
	getHeroCardOperationsImage,
	getHeroCardSoftwareDevelopmentImage,
} from '../assets/localized-images';

import { HeroCard } from './hero-card';
import { OrangeCard, PurpleCard, GreenCard, BlueCard, BlackCard } from './hero-card-backgrounds';
import { JswProductModalCarousel } from './product-modal-carousel';
import { HeroContextProvider } from './util/hero-context';

type HeroCardText = {
	eyebrow: string;
	title: string;
	image: string;
	label: string;
	BackgroundImage: () => JSX.Element;
};

export const HeroCards = () => {
	const intl = useIntl();

	const softwareCard = {
		eyebrow: intl.formatMessage({
			id: 'jira-product-tour.hero-cards-software-development-card.eyebrow-text',
			defaultMessage: 'product & issue tracking',
		}),
		title: intl.formatMessage({
			id: 'jira-product-tour.hero-cards-software-development-card.title-text',
			defaultMessage: 'Software Development',
		}),
		image: getHeroCardSoftwareDevelopmentImage(intl.locale),
		BackgroundImage: OrangeCard,
		label: 'software-development',
	};

	const marketingCard = {
		eyebrow: intl.formatMessage({
			id: 'jira-product-tour.hero-cards-marketing-card.eyebrow-text',
			defaultMessage: 'plan & launch campaigns',
		}),
		title: intl.formatMessage({
			id: 'jira-product-tour.hero-cards-marketing-card.title-text',
			defaultMessage: 'Marketing',
		}),
		image: getHeroCardMarketingImage(intl.locale),
		BackgroundImage: PurpleCard,
		label: 'marketing',
	};

	const itCard = {
		eyebrow: intl.formatMessage({
			id: 'jira-product-tour.hero-cards-it-card.updated-eyebrow-text',
			defaultMessage: 'plan & track it projects',
		}),
		title: intl.formatMessage({
			id: 'jira-product-tour.hero-cards-it-card.title-text',
			defaultMessage: 'IT',
		}),
		image: getHeroCardItImage(intl.locale),
		BackgroundImage: GreenCard,
		label: 'it',
	};

	const designCard = {
		eyebrow: intl.formatMessage({
			id: 'jira-product-tour.hero-cards-design-card.eyebrow-text',
			defaultMessage: 'build creative workflows',
		}),
		title: intl.formatMessage({
			id: 'jira-product-tour.hero-cards-design-card.title-text',
			defaultMessage: 'Design',
		}),
		image: getHeroCardDesignImage(intl.locale),
		BackgroundImage: BlueCard,
		label: 'design',
	};

	const operationsCard = {
		eyebrow: intl.formatMessage({
			id: 'jira-product-tour.hero-cards-operations-card.eyebrow-text',
			defaultMessage: 'create custom processes',
		}),
		title: intl.formatMessage({
			id: 'jira-product-tour.hero-cards-operations-card.title-text',
			defaultMessage: 'Operations',
		}),
		image: getHeroCardOperationsImage(intl.locale),
		BackgroundImage: BlackCard,
		label: 'operations',
	};

	const heroCardText: HeroCardText[] = [
		softwareCard,
		marketingCard,
		itCard,
		designCard,
		operationsCard,
	];

	return (
		<HeroContextProvider>
			<div css={heroCardsStyling}>
				{heroCardText.map((card, index) => (
					<HeroCard
						key={index}
						eyebrow={card.eyebrow}
						title={card.title}
						image={card.image}
						BackgroundImage={card.BackgroundImage as () => JSX.Element}
						idx={index}
						label={card.label}
					/>
				))}
				<JswProductModalCarousel />
			</div>
		</HeroContextProvider>
	);
};

const heroCardsStyling = css({
	display: 'flex',
	flexDirection: 'row',
	justifyContent: 'flex-start',
	width: '100%',
	maxWidth: 'fit-content',
	gap: '16px',
	overflowX: 'auto',
	marginInlineEnd: 'auto',
	marginInlineStart: 'auto',
	padding: '64px 10px 10px 10px',
	scrollbarColor: `${wacToken('color.neutral.dark800')} rgba(0,0,0,0)`,
	[media.below.lg]: {
		paddingBlockStart: '54px',
		justifyContent: 'flex-start',
		paddingBlockEnd: '10px',
		marginBlockStart: '3px',
		overflowX: 'scroll',
		scrollPaddingTop: '3px',
	},
});
